import React, { useCallback } from 'react';
import { Form } from 'react-bootstrap';
import { HubOption, HubOptionStateValue } from 'dorian-shared/types/branch/HubOption';
import type { ReplayDefaults } from 'dorian-shared/types/branch/HubOption';
import { CustomSelect } from '../../../../../CustomSelect';
import { useMemoryIconsContext } from 'contexts/MemoryIconsContext/MemoryIconsContext';
import { MemoryIconField } from '../../../../../../Book/MemoryBank/MemoryFields/MemoryIconField';
import { BranchSelectItem } from '../../shared/types';
import { parseGotoBranchId } from '../../shared/hubUtils';
import {
  createDefaultReplayDefaults,
  HUB_CARD_TEXT_MAX_LENGTH,
  HUB_CARD_TEXT_WARN_LENGTH,
  isReplayDefaultActive,
} from '../../hubStepDefaults';
import { HubClearButton } from '../../HubClearButton/HubClearButton';

type ReplayDefaultControlsProps = {
  replay: ReplayDefaults | null;
  branches: BranchSelectItem[];
  currentBranchId: number | null;
  isDisabled: boolean;
  onChange: (changes: Partial<HubOption>) => void;
  readOnly?: boolean;
};

export function ReplayDefaultControls({
  replay,
  branches,
  currentBranchId,
  isDisabled,
  onChange,
  readOnly = true,
}: ReplayDefaultControlsProps) {
  const { memoryIcons, createMemoryIcon } = useMemoryIconsContext();
  const replayGotoBranchId = replay?.gotoBranchId ?? null;
  const replayIconId = replay?.iconId ?? null;
  const replayText = replay?.text ?? 'Replay';

  const isActive = isReplayDefaultActive(replay);
  const showGotoSelect = !readOnly || isActive;
  const showIconField = !readOnly || (isActive && replayIconId != null);
  const showTextInput = !readOnly || isActive;
  const showClearButtons = !readOnly;

  const ensureReplay = useCallback(
    (): ReplayDefaults => replay ?? createDefaultReplayDefaults(),
    [replay],
  );

  const handleGotoChange = useCallback(
    (raw: unknown) => {
      const base = ensureReplay();
      const gotoBranchId = parseGotoBranchId(raw);
      const state = gotoBranchId != null ? HubOptionStateValue.Active : HubOptionStateValue.Hidden;
      onChange({ replay: { ...base, state, gotoBranchId } });
    },
    [ensureReplay, onChange],
  );

  const handleClearDestination = useCallback(() => {
    const base = ensureReplay();
    onChange({ replay: { ...base, state: HubOptionStateValue.Hidden, gotoBranchId: null } });
  }, [ensureReplay, onChange]);

  const handleClearIcon = useCallback(() => {
    const base = ensureReplay();
    onChange({ replay: { ...base, iconId: null } });
  }, [ensureReplay, onChange]);

  const handleIconChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const iconId = e.target.value ? Number(e.target.value) : null;
      const base = ensureReplay();
      onChange({ replay: { ...base, iconId } });
    },
    [ensureReplay, onChange],
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const base = ensureReplay();
      onChange({ replay: { ...base, text: e.target.value } });
    },
    [ensureReplay, onChange],
  );

  const showHiddenLabel = readOnly && !isActive;

  return (
    <div className="hub-option-replay__default-controls">
      {showHiddenLabel && (
        <span
          className="hub-option-replay__hidden-label"
          data-testid="replay-default-hidden-label"
        >
          HIDDEN
        </span>
      )}
      {showGotoSelect && (
        <CustomSelect
          disabled={readOnly || isDisabled}
          value={replayGotoBranchId ?? ''}
          onChange={handleGotoChange}
          placeholder="Hidden (no destination)"
          args={[
            { id: null, title: '' },
            { id: -1, title: '[Create New Node]' },
            ...branches,
          ]}
          data={currentBranchId}
        />
      )}
      {showClearButtons && (
        <HubClearButton
          onClick={handleClearDestination}
          disabled={isDisabled || replayGotoBranchId == null}
          title="Clear destination"
          data-testid="replay-clear"
        />
      )}
      {showIconField && (
        <div className="icon-field-inline">
          <MemoryIconField
            id="replay-default-icon"
            label=""
            memoryIcons={memoryIcons}
            onSave={createMemoryIcon}
            value={replayIconId != null ? String(replayIconId) : ''}
            onChange={handleIconChange}
            disabled={readOnly || isDisabled || replayGotoBranchId == null}
            variant="outline-primary"
          />
        </div>
      )}
      {showClearButtons && (
        <HubClearButton
          onClick={handleClearIcon}
          disabled={isDisabled || replayGotoBranchId == null || replayIconId == null}
          title="Clear icon"
          data-testid="replay-icon-clear"
        />
      )}
      {showTextInput && (
        <div className="hub-option-replay__default-text-input-wrap">
          <Form.Control
            size="sm"
            type="text"
            className={`hub-option-replay__default-text-input${replayText.length > HUB_CARD_TEXT_WARN_LENGTH ? ' text-warn' : ''}`}
            placeholder="Replay"
            value={replayText}
            onChange={handleTextChange}
            disabled={readOnly || isDisabled || replayGotoBranchId == null}
            maxLength={HUB_CARD_TEXT_MAX_LENGTH}
            data-testid="replay-default-text"
          />
          <Form.Text className="char-limit-info-box" style={{ top: '-8px', bottom: 'auto' }}>
            {HUB_CARD_TEXT_WARN_LENGTH - replayText.length}
          </Form.Text>
        </div>
      )}
    </div>
  );
}
